import { useCallback, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { getHtmlDomains } from '../../actions';
import {
  COOKIE_MAX_AGE,
  COOKIE_NAME,
  COOKIE_VERSION,
  mergeGroupDomains,
  PERFORMANCE_COOKIES_PREFIX,
} from '../../constants/Lgpd';

const withLgpdCookie = (OriginalComponent) => {
  function NewComponent(props) {
    const [cookies, setCookie, removeCookie] = useCookies([COOKIE_NAME]);
    const dispatch = useDispatch();
    const store = useStore();

    // Seleciona os domínios do Redux
    const htmlDomains = useSelector((state) => state.htmlDomains);

    // Carrega os domínios da API ao montar o componente
    useEffect(() => {
      // Lê o estado atual do store (e não o valor capturado no render) para que
      // várias instâncias montadas no mesmo commit disparem uma única requisição.
      const { loaded, loading } = store.getState().htmlDomains;
      if (!loaded && !loading) {
        dispatch(getHtmlDomains());
      }
    }, [dispatch, store, htmlDomains.loaded, htmlDomains.loading]);

    // Mescla os domínios hardcoded com os da API
    const groupDomains = mergeGroupDomains(
      htmlDomains.domainsWithConsent,
      htmlDomains.domainsWithoutConsent,
    );

    const toGovPlone4Cookie = useCallback(({ performance, thirdParty }) => {
      // BBB: Transforma os valores no formato do cookie do gov Plone 4.
      return {
        v: COOKIE_VERSION,
        g: [
          { id: 'cookies-estritamente-necessarios', on: true },
          { id: 'cookies-de-desempenho', on: performance },
          { id: 'cookies-de-terceiros', on: thirdParty },
        ],
      };
    }, []);

    const removePerformanceCookies = useCallback(() => {
      Object.keys(cookies).forEach((cookieName) => {
        PERFORMANCE_COOKIES_PREFIX.forEach((prefix) => {
          if (cookieName.startsWith(prefix)) {
            removeCookie(cookieName);
            // Tratamento para o ambiente de testes
            removeCookie(cookieName, { domain: '.serpro.gov.br' });
          }
        });
      });
    }, [cookies, removeCookie]);

    const writeCookie = useCallback(
      (value) => {
        setCookie(
          COOKIE_NAME,
          { ...toGovPlone4Cookie(value) },
          {
            maxAge: COOKIE_MAX_AGE,
            encode: (v) => v,
            path: '/',
          },
        );
        !value.performance && removePerformanceCookies();
        window.location.reload();
      },
      [setCookie, toGovPlone4Cookie, removePerformanceCookies],
    );

    const acceptCookies = useCallback(() => {
      writeCookie({
        performance: true,
        thirdParty: true,
      });
    }, [writeCookie]);

    const rejectCookies = useCallback(() => {
      writeCookie({
        performance: false,
        thirdParty: false,
      });
    }, [writeCookie]);

    const saveCookies = useCallback(
      ({ performance, thirdParty }) => {
        writeCookie({
          performance,
          thirdParty,
        });
      },
      [writeCookie],
    );

    const lgpdCookie = cookies[COOKIE_NAME];
    let hasLgpdCookie = !!lgpdCookie;

    if (hasLgpdCookie && lgpdCookie.v !== COOKIE_VERSION) {
      // TODO: remover dentro de m useEffect - testar!!
      // removeCookie(COOKIE_NAME);
      hasLgpdCookie = false;
    }

    const performanceCookies = hasLgpdCookie && lgpdCookie.g[1].on;
    const thirdPartyCookies = hasLgpdCookie && lgpdCookie.g[2].on;

    return (
      <OriginalComponent
        {...props}
        performanceCookies={performanceCookies}
        thirdPartyCookies={thirdPartyCookies}
        hasLgpdCookie={hasLgpdCookie}
        isClient={__CLIENT__}
        acceptCookies={acceptCookies}
        rejectCookies={rejectCookies}
        saveCookies={saveCookies}
        removePerformanceCookies={removePerformanceCookies}
        groupDomains={groupDomains}
      />
    );
  }
  return NewComponent;
};
export default withLgpdCookie;
